'use client'

import { useTransition } from "react"
import type { JSX } from "react"
import { Form } from "../forms"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { useToast } from "../../hooks";
import { Logger } from "@shared/logger"
import { UI_ACTIONS } from '@shared/constants'
import { tryCatchAsync } from "@shared/utils"
import { useOnboardingFlow } from "./hooks/useOnboardingFlow"
import { EmailField, PasswordField, AuthSubmitButton, OnboardingBlockedMessage } from "./components"

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters.",
  })
})

/**
 * @group Interfaces
 * 
 * @summary
 * <blockquote>Props for the AuthForm component that handles both sign-in and sign-up functionality.</blockquote>
 * 
 * @example
 * ```typescript
 * const props: AuthFormProps = {
 *   mode: 'signup',
 *   useServerAction: true
 * };
 * ```
 */
interface AuthFormProps {
  mode: 'signin' | 'signup'
  useServerAction?: boolean
  tenantUUID?: string
}

/**
 * `AuthForm` provides a reusable authentication form for signing in or signing up users.
 *
 * - Validates email and password using Zod and react-hook-form.
 * - On successful signup, automatically signs the user in.
 * - Displays form errors, toast notifications, and loading indicators.
 *
 * @param mode - Choose between `'signin'` and `'signup'` behavior.
 */
export function AuthForm({ mode, useServerAction = false, tenantUUID }: AuthFormProps): JSX.Element {
  const [isPending, startTransition] = useTransition()
  const { toast } = useToast()
  
  const {
    onboardingBlockedMessage,
    clearOnboardingBlockedMessage,
    handleSignup,
    handleSignIn,
  } = useOnboardingFlow({
    tenantUUID,
    useServerAction,
    toast,
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  /**
   * Handles form submission for both sign-in and sign-up modes.
   */
  function onSubmit(values: z.infer<typeof formSchema>): void {
    startTransition(async () => {
      clearOnboardingBlockedMessage();
      const result = await tryCatchAsync(async () => {
        if (mode === UI_ACTIONS.AUTH.SIGNUP) {
          await handleSignup(values)
        } else {
          await handleSignIn(values)
        }
      })

      if (result.isErr) {
        const error = result.getFail();
        Logger.error('Authentication error', { resource: 'auth.signup' }, error instanceof Error ? error : new Error(String(error)))
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "An unexpected error occurred",
          variant: "destructive",
        })
      }
    })
  }

  return (
    <div className="w-full max-w-[400px] space-y-8 px-4 sm:px-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <EmailField control={form.control} disabled={isPending} />
          <PasswordField control={form.control} disabled={isPending} />
          <AuthSubmitButton mode={mode} isPending={isPending} />
        </form>
      </Form>

      <div className="text-center text-sm">
        <OnboardingBlockedMessage message={onboardingBlockedMessage} />
        {/* Temporarily disabled sign up functionality */}
        {/* {mode === UI_ACTIONS.AUTH.SIGNIN ? (
          <>
            Don&apos;t have an account?{" "}
            <Link href="/users/signup" className="underline underline-offset-4 hover:text-primary">
              Sign up
            </Link>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <Link href="/users/signin" className="underline underline-offset-4 hover:text-primary">
              Sign in
            </Link>
          </>
        )} */}
      </div>
    </div>
  )
}
